import React, { useState, useEffect, useCallback } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";
import { requestResetPassword } from "./api-auth.js";
import { findByHostname } from "../components/Instituicao/api-instituicao";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import AuthLayout, { AuthFormHeading } from "./AuthLayout";

// Mapeia trechos da mensagem de erro da API para uma mensagem amigável.
// Trocar o if/else por uma tabela deixa fácil adicionar novos casos sem
// aninhar mais condicionais.
const ERROR_MESSAGES = [
  ["User not found", "Não existe usuário cadastrado com esse e-mail."],
  [
    "More than 3 attempts",
    "Já foram feitas 3 tentativas. Espere 24 horas para fazer uma nova tentativa.",
  ],
];

function resolveErrorMessage(apiMessage) {
  const match = ERROR_MESSAGES.find(([needle]) => apiMessage?.includes(needle));
  return match
    ? match[1]
    : "Não foi possível enviar o e-mail. Tente novamente.";
}

const SUCCESS_MESSAGE =
  "E-mail enviado. Acesse sua conta de e-mail e siga os passos para resetar a sua senha. " +
  "O link para resetar a senha ficará ativo por 1 hora; caso a troca não seja realizada " +
  "nesse período, o processo poderá ser repetido no dia seguinte.";

export default function RequestResetPasswordV2() {
  const [email, setEmail] = useState("");
  const [instituicao, setInstituicao] = useState(null);
  const [feedback, setFeedback] = useState(null); // { severity: "success" | "error", message }
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    document.title = "Resetar Senha";
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    findByHostname(window.location.href, { signal: controller.signal })
      .then((data) => {
        if (data && !data.error) setInstituicao(data);
      })
      .catch((err) => {
        if (err.name !== "AbortError") console.error(err);
      });

    return () => controller.abort();
  }, []);

  const handleChange = useCallback((event) => {
    setEmail(event.target.value);
  }, []);

  const clickSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (isLoading) return; // evita duplo envio

      setIsLoading(true);
      setFeedback(null);
      try {
        const data = await requestResetPassword(email);
        if (!data) return;

        if (data.error) {
          console.error(data);
          setFeedback({
            severity: "error",
            message: resolveErrorMessage(data.message),
          });
        } else {
          setFeedback({ severity: "success", message: SUCCESS_MESSAGE });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [email, isLoading],
  );

  return (
    <AuthLayout
      eyebrow={instituicao?.nome || "Portal Acadêmico"}
      headline="Vamos recuperar seu acesso."
      logoUrl={instituicao?.urlLogo}
    >
      <AuthFormHeading
        title="Resetar senha"
        subtitle="Informe seu e-mail cadastrado para receber o link de redefinição."
      />

      <form onSubmit={clickSubmit} noValidate>
        <TextField
          margin="normal"
          required
          fullWidth
          type="email"
          label="Email"
          name="username"
          autoComplete="email"
          autoFocus
          disabled={isLoading}
          onChange={handleChange}
          value={email}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MailOutlineIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : null
          }
          sx={{ mt: 3, py: 1.3 }}
        >
          {isLoading ? "Enviando..." : "Enviar e-mail"}
        </Button>

        {feedback && (
          <Alert
            severity={feedback.severity === "success" ? "success" : "error"}
            sx={{ mt: 3 }}
          >
            {feedback.message}
          </Alert>
        )}
      </form>
    </AuthLayout>
  );
}
